// Sensitivity 탭 — 바닐라 computeSensGrid + renderSensitivity 이식.
// g축은 0~3%(0.5% 단위) 고정, WACC축은 엔진 산출(result.sensitivity.wacc_axis) 우선.
// result JSON 소비 필드: sensitivity.wacc_axis, wacc.value, periods[].midpoint_years,
// fcff.fcff, terminal.{fcff_terminal_input,g}, bridge.{non_operating_assets,
// interest_bearing_debt,preferred_stock_amount,pv_tax_shield,
// diluted_shares_outstanding,shares_outstanding,unit_multiplier_to_won}
import { fmt } from "../../lib/format";
import { useT } from "../../lib/i18n";
import { useDcf } from "../../stores/dcf";
import type { DcfPayload, DcfResult } from "../../types/dcf";

const SENS_G_AXIS = [0, 0.005, 0.01, 0.015, 0.02, 0.025, 0.03];

interface SensCell {
  wacc: number;
  g: number;
  price_per_share: number;
}
interface SensGrid {
  wacc_axis: number[];
  g_axis: number[];
  matrix: SensCell[][]; // [wacc][g]
}

function computeSensGrid(r: DcfResult, payload: DcfPayload): SensGrid {
  const waxis =
    r.sensitivity?.wacc_axis && r.sensitivity.wacc_axis.length
      ? r.sensitivity.wacc_axis
      : (() => {
          const w = r.wacc?.value ?? payload.discount_rate ?? 0.11;
          const st = 0.005;
          return [w - 2 * st, w - st, w, w + st, w + 2 * st];
        })();
  const periods = r.periods || [];
  const fcff = r.fcff?.fcff || [];
  const mids = periods.map((p) => p.midpoint_years);
  const fallbackLast = fcff.length ? fcff.length - 0.5 : 5;
  const lastMid = mids.length ? (mids[mids.length - 1] ?? fallbackLast) : fallbackLast;
  const t = r.terminal || {};
  const fTerm =
    t.fcff_terminal_input != null
      ? t.fcff_terminal_input
      : fcff.length
        ? fcff[fcff.length - 1]
        : 0;
  const b = r.bridge || {};
  const nonop = b.non_operating_assets || 0;
  const debt = b.interest_bearing_debt || 0;
  const pref = b.preferred_stock_amount || 0;
  const taxShield = b.pv_tax_shield || 0;
  const shares = b.diluted_shares_outstanding || b.shares_outstanding || 1;
  const mult = b.unit_multiplier_to_won || 1000000;

  const matrix: SensCell[][] = waxis.map((w) =>
    SENS_G_AXIS.map((g) => {
      let sumpv = 0;
      for (let i = 0; i < fcff.length; i++) {
        const m = mids[i] ?? i + 0.5;
        sumpv += fcff[i] / Math.pow(1 + w, m);
      }
      const pvifLast = 1 / Math.pow(1 + w, lastMid);
      const tv = w > g ? (fTerm * (1 + g)) / (w - g) : 0;
      const op = sumpv + tv * pvifLast + taxShield;
      const pps = ((op + nonop - debt - pref) / shares) * mult;
      return { wacc: w, g, price_per_share: pps };
    }),
  );
  return { wacc_axis: waxis, g_axis: SENS_G_AXIS, matrix };
}

export default function SensitivityTab() {
  const { t } = useT();
  const current = useDcf((s) => s.current);
  if (!current) return null;
  const p = current.payload;
  const r = current.result;
  if (!r) {
    return (
      <div className="data-card">
        <h4>{t("h.sensitivity")}</h4>
        <p className="hint">{t("ph.recalc_show")}</p>
      </div>
    );
  }

  const baseW = r.wacc?.value ?? p.discount_rate ?? 0;
  const baseG = (r.terminal?.g ?? p.terminal_growth_rate) ?? 0;
  const s = computeSensGrid(r, p);

  const near = (a: number, b: number) => Math.abs(a - b) < 0.0006;
  const wi0 = s.wacc_axis.findIndex((w) => near(w, baseW));
  const gi0 = s.g_axis.findIndex((g) => near(g, baseG));
  const basePps =
    wi0 >= 0 && gi0 >= 0 ? s.matrix[wi0][gi0].price_per_share || 0 : 0;

  // 바닐라 renderSensitivity 하단 해석 문구
  let comment = t("ph.recalc_auto");
  if (wi0 >= 0 && gi0 >= 0) {
    const base = s.matrix[wi0][gi0];
    const wUp = s.matrix[Math.min(wi0 + 1, s.wacc_axis.length - 1)][gi0];
    const gUp = s.matrix[wi0][Math.min(gi0 + 1, s.g_axis.length - 1)];
    if (base.price_per_share) {
      const wS = Math.abs(
        ((wUp.price_per_share - base.price_per_share) / base.price_per_share) * 100,
      );
      const gS = Math.abs(
        ((gUp.price_per_share - base.price_per_share) / base.price_per_share) * 100,
      );
      comment = `WACC 1단계 상승 시 주당가치 약 ${wS.toFixed(1)}% 변동, 영구성장률 1단계 상승 시 약 ${gS.toFixed(1)}% 변동합니다. 본 평가는 ${wS > gS ? "WACC" : "영구성장률"} 가정에 상대적으로 더 민감합니다.`;
    }
  }

  return (
    <>
      <div className="data-card">
        <h4>{t("h.sensitivity")}</h4>
        <div style={{ overflowX: "auto" }}>
          <table className="sens-table">
            <thead>
              <tr>
                <th>g \ WACC</th>
                {s.wacc_axis.map((w, wi) => (
                  <th key={wi}>{(w * 100).toFixed(1)}%</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.g_axis.map((g, gi) => (
                <tr key={gi}>
                  <td className="row-header">{(g * 100).toFixed(1)}%</td>
                  {s.wacc_axis.map((w, wi) => {
                    const pps = s.matrix[wi][gi].price_per_share;
                    const isBase = near(w, baseW) && near(g, baseG);
                    const ratio = basePps > 0 && pps != null ? pps / basePps : 1;
                    const cls = isBase
                      ? "base-cell"
                      : ratio > 1.1
                        ? "sens-val-high"
                        : ratio > 0.9
                          ? "sens-val-mid"
                          : "sens-val-low";
                    return (
                      <td key={wi} className={cls}>
                        {pps != null ? fmt(pps) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="agent-comment">
        <div className="comment-header">{t("lbl.sens_interp")}</div>
        <p>{comment}</p>
      </div>
    </>
  );
}
